import { CSSProperties, ReactNode } from "react"

import { MediaConfigScope, MediaType } from "../../GlobalMediaTypes/types"
import { css, cx } from "@emotion/css"

import Button from "ui/buttons/Button"
import { ReactComponent as CinemaIcon } from "assets/mediaTypes/icon-media-types-cinema.svg"
import { ReactComponent as DeleteIcon } from "assets/delete.svg"
import { ReactComponent as Digital } from "assets/mediaTypes/icon-media-types-digital.svg"
import { ReactComponent as EventIcon } from "assets/mediaTypes/icon-media-types-event.svg"
import { ReactComponent as MobileIcon } from "assets/mediaTypes/icon-media-types-mobile.svg"
import MoreActions from "components/MoreActions"
import { ReactComponent as NewspaperIcon } from "assets/mediaTypes/icon-media-types-newspaper.svg"
import { ReactComponent as OutOfHomeIcon } from "assets/mediaTypes/icon-media-types-out-of-home.svg"
import { ReactComponent as PrintIcon } from "assets/mediaTypes/icon-media-types-print.svg"
import { ReactComponent as RadioIcon } from "assets/mediaTypes/icon-media-types-radio.svg"
import { Tooltip } from "@material-ui/core"
import { ReactComponent as TvIcon } from "assets/mediaTypes/icon-media-types-tv.svg"
import { WithAuthority } from "components/WithAuthority"
import { useI18n } from "providers/I18n"

type Props = {
  data: MediaType[]
  selectedId?: number
  onOpen: (_: any, mediaType?: MediaType | undefined, id?: number) => void
  onDelete: (id: string) => void
  configScope: MediaConfigScope
}

const icons_per_type: Record<string, ReactNode> = {
  cinema: <CinemaIcon />,
  event: <EventIcon />,
  mobile: <MobileIcon />,
  newspaper: <NewspaperIcon />,
  out_of_home: <OutOfHomeIcon />,
  print: <PrintIcon />,
  radio: <RadioIcon />,
  tv: <TvIcon />,
  digital: <Digital />,
}

const hasNoSubMedias = ["AGENCY"]
const hasNoFields = ["SUPPLIER", "AGENCY"]
const hasNoStatus = ["SUPPLIER", "SUPPLIER_GROUP", "AGENCY"]
/* const hasChannels = ["SUPPLIER"] */

export const MediaTypesList = (props: Props) => {
  const { t } = useI18n()
  const { data, selectedId, onOpen, onDelete, configScope } = props

  return !!data?.length ? (
    <>
      {data?.map((d, idx) => {
        const subMedia = d?.submediaTypes?.map((sm) => sm?.name)
        const gaps = d?.activationGaps?.map((gap, index) => {
          return (
            <div
              key={index}
              style={{ marginBottom: index < 2 ? "5px" : undefined }}
            >
              {gap.toString()}
            </div>
          )
        })
        const tooltipTitle = <div>{gaps}</div>

        return (
          <div
            className={cx(styles.container, {
              [styles.selectedMediaTypes]: idx === selectedId,
            })}
            key={idx}
            onClick={(e) => onOpen(e, d, idx)}
          >
            <span
              className={styles.dot}
              style={
                {
                  "--color": `${d?.color || "#48718c"}`,
                } as CSSProperties
              }
            >
              {!!d?.semanticCode && icons_per_type[d?.semanticCode]}
            </span>
            <div className={cx(styles.columnWrapper, styles.mediaWrapper)}>
              {!hasNoStatus.includes(configScope) ? (
                <div className={styles.row}>
                  <span className="BodyBody-2BlackBold">{d.name}</span>
                  {d?.status === "ACTIVE" ? (
                    <span
                      className={cx("BodyBody-xsWhiteRegular", styles.active)}
                    >
                      {d.status}
                    </span>
                  ) : (
                    <>
                      <Tooltip
                        title={tooltipTitle}
                        classes={{
                          tooltip: cx("BodyBody-sWhiteRegular", styles.tooltip),
                        }}
                      >
                        <span
                          className={cx(
                            "BodyBody-xsWhiteRegular",
                            styles.inactive,
                          )}
                        >
                          {t("status.INACTIVE")}
                        </span>
                      </Tooltip>
                    </>
                  )}
                </div>
              ) : null}
              <span className={cx("BodyBody-2BlackLight", styles.text)}>
                {d.semanticName}
              </span>
            </div>
            {!hasNoSubMedias.includes(configScope) ? (
              <div className={styles.columnWrapper}>
                <span className={cx("BodyBody-2BlackBold", styles.title)}>
                  {t("admin.mediaTypes.subMediaTypes")}
                  {d?.submediaTypes?.length
                    ? ` (${d?.submediaTypes?.length})`
                    : null}
                </span>
                {d?.submediaTypes?.length ? (
                  <span className={cx("BodyBody-2BlackLight", styles.text)}>
                    {d?.submediaTypes?.length > 1
                      ? subMedia?.join(", ")
                      : subMedia}
                  </span>
                ) : (
                  <Button color="primary" className={styles.button}>
                    {t("add")}
                  </Button>
                )}
              </div>
            ) : null}
            {!hasNoFields.includes(configScope) ? (
              <div className={styles.columnWrapper}>
                <span className={cx("BodyBody-2BlackBold", styles.title)}>
                  {t("admin.mediaTypes.fields")}
                  {d?.mediaFields ? ` (${d?.mediaFields.length})` : null}
                </span>
                {d?.mediaFields ? (
                  <Button color="primary" className={styles.button}>
                    {t("button.view")}
                  </Button>
                ) : (
                  <Button color="primary" className={styles.button}>
                    {t("button.add")}
                  </Button>
                )}
              </div>
            ) : null}
            {/*  {hasChannels.includes(configScope) ? (
              <div className={styles.columnWrapper}>
                <span className={cx("BodyBody-2BlackBold", styles.title)}>
                  {t("admin.mediaTypes.tabs.channels")}
                  {d?.mediaFields ? ` (${d?.mediaFields.length})` : null}
                </span>
                {d?.mediaFields ? (
                  <Button color="primary" className={styles.button}>
                    {t("button.view")}
                  </Button>
                ) : (
                  <Button color="primary" className={styles.button}>
                    {t("button.add")}
                  </Button>
                )}
              </div>
            ) : null} */}
            <div className={styles.moreActions}>
              <WithAuthority allowed={["AUTH_DELETE_MEDIA_TYPE"]}>
                <MoreActions
                  options={[
                    // DELETE
                    {
                      label: t("actions.delete"),
                      icon: <DeleteIcon />,
                      onClick: () => {
                        onDelete(d?.id)
                      },
                    },
                  ]}
                />
              </WithAuthority>
            </div>
          </div>
        )
      })}
    </>
  ) : (
    <></>
  )
}

const styles = {
  container: css`
    width: 100%;
    padding: var(--spacing-xl) var(--spacing-m);
    border: 1px solid var(--light-grey);
    margin-bottom: var(--spacing-xs);
    border-radius: 2px;
    display: flex;
    align-items: flex-start;
    height: 108px;
    cursor: pointer;
    &:hover {
      border: 1px solid var(--primary-main);
    }
  `,
  selectedMediaTypes: css`
    border: 2px solid var(--primary-main);
  `,
  dot: css`
    --color: var(--secondary-light-blue);
    position: relative;
    width: 56px;
    height: 56px;
    line-height: 1rem;
    border-radius: 50%;
    background-color: var(--color);
    margin-right: 20px;
    > svg {
      position: absolute;
      top: 12px;
      left: 20px;
    }
  `,
  columnWrapper: css`
    display: flex;
    flex-direction: column;
    width: 30%;
    margin-right: 20px;
  `,
  mediaWrapper: css`
    width: 20%;
    padding-left: var(--spacing-m);
  `,
  text: css`
    padding-top: 2px;
  `,
  title: css`
    display: flex;
    align-items: center;
  `,
  button: css`
    --btn-padding: 0;
    --btn-min-width: 0;
    --btn-line-height: 1;
    --btn-color: inherit;
    --btn-font-weight: normal;
    --btn-bg-hover: transparent;
    --btn-bg-active: transparent;
    --btn-border-color-hover: transparent;
    --btn-bg-focus-ring-color: transparent;
    width: fit-content;
    &:focus {
      box-shadow: none;
    }
    padding-top: 2px;
  `,
  moreActions: css`
    margin: 0 0 auto auto;
  `,
  row: css`
    display: flex;
    flex-direction: row;
  `,
  active: css`
    background-color: var(--primary-main);
    padding: 2px var(--spacing-xs) 0;
    margin-left: var(--spacing-xs);
  `,
  inactive: css`
    background-color: var(--red);
    padding: 2px var(--spacing-xs) 0px;
    margin-left: var(--spacing-xs);
    text-transform: uppercase;
    box-shadow: 0 2px 4px 0 var(--light-grey);
  `,
  tooltip: css`
    background-color: var(--red);
    border-radius: 2px;
  `,
}
